import { useState, useEffect } from 'react';
import { dbService } from '@/services/db/index';
import { Admin } from '@/types/admin.types';
import { toast } from 'sonner';
import { universalStorage } from '@/services/storage/universalStorage';
import { adminFileStorageService } from '@/services/db/admins/fileStorageService';

export const useAdminData = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all-roles');
  const [initialized, setInitialized] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  // Derived state
  const loading = isLoading;
  const error = null; // Simplified for localStorage-only setup
  const roles = ['Admin', 'Super Admin', 'Manager']; // Common admin roles

  // Filter admins based on filters
  const filteredAdmins = admins.filter(admin => {
    const matchesText = !filterText || 
      `${admin.firstName} ${admin.lastName}`.toLowerCase().includes(filterText.toLowerCase()) ||
      admin.email?.toLowerCase().includes(filterText.toLowerCase()) ||
      admin.position?.toLowerCase().includes(filterText.toLowerCase());
    
    const matchesRole = roleFilter === 'all-roles' || admin.role === roleFilter;
    
    return matchesText && matchesRole;
  });

  const loadAdmins = async (showToast = false) => {
    try {
      setIsLoading(true);
      console.log('👥 Loading admins from localStorage ONLY (5000+ capacity)...');
      
      // localStorage storage check before loading
      const storageInfo = await universalStorage.getStorageInfo();
      if (storageInfo.usage && storageInfo.usage.used > storageInfo.usage.quota * 0.9) {
        console.log('📦 Optimizing localStorage for 5000+ admin capacity...');
        await universalStorage.getStorageMetrics();
      }
      
      const adminData = await dbService.getAllAdmins();
      console.log(`✅ Loaded ${adminData.length} admins from localStorage`);
      
      setAdmins(adminData);
      setDataVersion(prev => prev + 1); // Trigger re-render for localStorage persistence
      
      if (showToast) {
        toast.success(`Loaded ${adminData.length} admins from localStorage (Total capacity: up to 5000 admins)`);
      }
    } catch (error) {
      console.error('Error loading admins from localStorage:', error);
      
      if (error instanceof Error && (error.message.includes('quota') || error.message.includes('storage'))) {
        if (showToast) {
          toast.error('localStorage optimized automatically. Admins now support 5000+ records.');
        }
      } else if (showToast) {
        toast.error('Failed to load admins from localStorage');
      }
      
      // Set empty array to prevent undefined errors
      setAdmins([]);
    } finally {
      setIsLoading(false);
    }
  };

  const initializeAdminData = async () => {
    console.info('🚀 Initializing admin data using localStorage ONLY (5000+ capacity)...');
    setIsLoading(true);
    
    try {
      // Clear any stale cache for fresh localStorage load
      adminFileStorageService.clearCache();
      
      await loadAdmins();
      setInitialized(true);
      
      // Set up localStorage persistence monitoring
      const persistenceCheck = setInterval(async () => {
        try {
          // Simple persistence check without getCacheStatus
          const currentAdmins = await adminFileStorageService.getAllAdmins();
          if (currentAdmins.length !== admins.length) {
            console.log('🔄 Admin data change detected, refreshing...');
            await loadAdmins();
          }
        } catch (error) {
          console.error('Error in admin localStorage persistence check:', error);
        }
      }, 60000); // Check every minute

      // Cleanup interval on unmount
      return () => clearInterval(persistenceCheck);
    } catch (error) {
      console.error('❌ Error initializing admin data from localStorage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAdmins = async () => {
    setIsRefreshing(true);
    try {
      // Clear cache to ensure fresh data from localStorage
      adminFileStorageService.clearCache();
      await loadAdmins(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const addAdmin = async (adminData: Omit<Admin, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      console.log('👥 Adding new admin to localStorage ONLY (5000+ capacity)...');
      
      // localStorage storage check before adding
      const storageInfo = await universalStorage.getStorageInfo();
      if (storageInfo.usage && storageInfo.usage.used > storageInfo.usage.quota * 0.9) {
        console.log('📦 Optimizing localStorage for new admin...');
        await universalStorage.getStorageMetrics();
      }
      
      const newAdmin = await dbService.addAdmin({
        ...adminData,
        id: `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        syncStatus: 'synced'
      });

      if (newAdmin) {
        await refreshAdmins();
        toast.success(`Admin ${newAdmin.firstName} ${newAdmin.lastName} added to localStorage (Total capacity: up to 5000 admins)`);
        
        // Emit change event for real-time updates using localStorage
        window.dispatchEvent(new CustomEvent('adminDataChanged', { 
          detail: { action: 'added', adminId: newAdmin.id, dataVersion } 
        }));
        
        return newAdmin;
      }
    } catch (error) {
      console.error('Error adding admin to localStorage:', error);
      
      if (error instanceof Error && (error.message.includes('quota') || error.message.includes('storage'))) {
        toast.error('localStorage optimized automatically. Please try again - the system now supports 5000+ admins.');
      } else {
        toast.error('Failed to add admin to localStorage');
      }
      throw error;
    }
  };

  const updateAdmin = async (id: string, updates: Partial<Admin>) => {
    try {
      const existingAdmin = admins.find(admin => admin.id === id);
      if (!existingAdmin) {
        throw new Error('Admin not found in localStorage');
      }

      // localStorage storage check before updating
      const storageInfo = await universalStorage.getStorageInfo();
      if (storageInfo.usage && storageInfo.usage.used > storageInfo.usage.quota * 0.9) {
        console.log('📦 Optimizing localStorage for admin update...');
        await universalStorage.getStorageMetrics();
      }

      const updatedAdminData = {
        ...existingAdmin,
        ...updates,
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        syncStatus: 'synced'
      };

      await dbService.updateAdmin(id, updatedAdminData);
      await refreshAdmins();
      toast.success('Admin updated in localStorage (Enhanced 5000+ capacity)');
      
      // Emit change event for real-time updates using localStorage
      window.dispatchEvent(new CustomEvent('adminDataChanged', { 
        detail: { action: 'updated', adminId: id, dataVersion } 
      }));
      
      return updatedAdminData;
    } catch (error) {
      console.error('Error updating admin in localStorage:', error);
      
      if (error instanceof Error && (error.message.includes('quota') || error.message.includes('storage'))) {
        toast.error('localStorage optimized automatically. Please try again - the system now supports 5000+ admins.');
      } else {
        toast.error('Failed to update admin in localStorage');
      }
      throw error;
    }
  };

  const deleteAdmin = async (id: string) => {
    try {
      await dbService.deleteAdmin(id);
      await refreshAdmins();
      toast.success('Admin deleted from localStorage (Enhanced 5000+ capacity)');
      
      // Emit change event for real-time updates using localStorage
      window.dispatchEvent(new CustomEvent('adminDataChanged', { 
        detail: { action: 'deleted', adminId: id, dataVersion } 
      }));
    } catch (error) {
      console.error('Error deleting admin from localStorage:', error);
      
      if (error instanceof Error && (error.message.includes('quota') || error.message.includes('storage'))) {
        toast.error('localStorage optimized automatically. Please try again - the system now supports 5000+ admins.');
      } else {
        toast.error('Failed to delete admin from localStorage');
      }
      throw error;
    }
  };

  // Listen for real-time admin data changes using localStorage
  useEffect(() => {
    const handleAdminDataChange = (event: CustomEvent | Event) => {
      console.log('🔄 Admin data change detected in localStorage, refreshing...');
      loadAdmins();
    };

    window.addEventListener('adminDataChanged', handleAdminDataChange);

    // Also listen for storage events for cross-tab localStorage persistence
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'nalc_admins') {
        console.log('🔄 Admin localStorage change detected, refreshing...');
        adminFileStorageService.clearCache();
        loadAdmins();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('adminDataChanged', handleAdminDataChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dataVersion]);

  // Listen for app focus to refresh data for localStorage persistence
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && initialized) {
        console.log('🔄 App focused, refreshing admin data from localStorage...');
        loadAdmins();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [initialized]);

  useEffect(() => {
    if (!initialized) {
      initializeAdminData();
    }
  }, [initialized]);

  return {
    admins: filteredAdmins,
    isLoading,
    isRefreshing,
    loading,
    error,
    filterText,
    setFilterText,
    roleFilter,
    setRoleFilter,
    roles,
    refreshAdmins,
    addAdmin,
    updateAdmin,
    deleteAdmin,
    initialized,
    initializeAdminData,
    dataVersion // For localStorage persistence tracking
  };
};
